import React, { useEffect } from "react"
import { useParams } from "react-router-dom"
import { Container, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, FormGroup, Label } from "reactstrap"
import { cancelOrder, closeOrder, editOrder, getOrder, generateRefundCode, refundOrder } from "../../api"
import styles from "./OrderDetailsPage.module.css"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"

const OrderDetailsPage = ({ t }) => {
  document.title = t("Order Details")
  const { orderId } = useParams()
  const [orderDetails, setOrderDetails] = React.useState(null)
  const [txId, setTxId] = React.useState("")
  const [cryptoCurrencyCode, setCryptoCurrencyCode] = React.useState("")
  const [cryptoAmount, setCryptoAmount] = React.useState("")
  const [cryptoRate, setCryptoRate] = React.useState("")
  const [fiatAmount, setFiatAmount] = React.useState("")
  const [profit, setProfit] = React.useState("")
  const [orderType, setOrderType] = React.useState("")
  const [editable, setEditable] = React.useState(false)

  const [changesWasMade, setChangesWasMade] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [saveError, setSaveError] = React.useState("")
  const [closing, setClosing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [calculationMode, setCalculationMode] = React.useState("byAmount") // "byAmount" or "byRate"
  const [showConfirmModal, setShowConfirmModal] = React.useState(false)
  const [showRefundModal, setShowRefundModal] = React.useState(false)
  const [refundCode, setRefundCode] = React.useState("")
  const [refunding, setRefunding] = React.useState(false)
  const [refundError, setRefundError] = React.useState("")

  function handleChanges(e) {
    setSaved(false)
    setSaveError("")
    const { name, value } = e.target

    // Handle field updates
    switch (name) {
      case "txId":
        setTxId(value)
        setChangesWasMade(true)
        break
      case "cryptoCurrencyCode":
        setCryptoCurrencyCode(value)
        setChangesWasMade(true)
        break
      case "orderType":
        setOrderType(value)
        setChangesWasMade(true)
        break
      case "cryptoAmount":
        setCryptoAmount(value)
        setChangesWasMade(true)
        if (calculationMode === "byAmount") {
          autoCalculateByAmount(parseFloat(value) || 0, parseFloat(fiatAmount) || 0)
        } else {
          autoCalculateByRate(parseFloat(cryptoRate) || 0, parseFloat(value) || 0, null)
        }
        break
      case "fiatAmount":
        setFiatAmount(value)
        setChangesWasMade(true)
        if (calculationMode === "byAmount") {
          autoCalculateByAmount(parseFloat(cryptoAmount) || 0, parseFloat(value) || 0)
        } else {
          autoCalculateByRate(parseFloat(cryptoRate) || 0, null, parseFloat(value) || 0)
        }
        break
      case "cryptoRate":
        setCryptoRate(value)
        setChangesWasMade(true)
        if (calculationMode === "byRate") {
          autoCalculateByRate(parseFloat(value) || 0, parseFloat(cryptoAmount) || 0, parseFloat(fiatAmount) || 0)
        }
        break
      case "profit":
        setProfit(value)
        setChangesWasMade(true)
        break
      default:
        break
    }
  }

  function autoCalculateByAmount(cryptoAmt, fiatAmt) {
    if (cryptoAmt > 0 && fiatAmt > 0 && orderType) {
      let profitVal = 0
      let rateVal = 0

      if (orderType === "buy") {
        // BUY: profit = fiat - crypto, rate% = profit / fiat * 100
        profitVal = fiatAmt - cryptoAmt
        rateVal = (profitVal / fiatAmt) * 100
      } else {
        // SELL: profit = crypto - fiat, rate% = profit / crypto * 100
        profitVal = cryptoAmt - fiatAmt
        rateVal = (profitVal / cryptoAmt) * 100
      }

      setProfit(profitVal.toFixed(2))
      setCryptoRate(rateVal.toFixed(2))
    }
  }

  function autoCalculateByRate(rate, cryptoAmt, fiatAmt) {
    if (!isNaN(rate) && orderType) {
      if (orderType === "buy") {
        // BUY: input Fiat Amount + Rate, calculate Crypto Amount and Profit
        if (fiatAmt > 0) {
          const profitVal = fiatAmt * (rate / 100)
          const cryptoAmtCalc = fiatAmt - profitVal
          setProfit(profitVal.toFixed(2))
          setCryptoAmount(cryptoAmtCalc.toFixed(2))
        }
      } else {
        // SELL: input Crypto Amount + Rate, calculate Fiat Amount and Profit
        if (cryptoAmt > 0) {
          const profitVal = cryptoAmt * (rate / 100)
          const fiatAmtCalc = cryptoAmt - profitVal
          setProfit(profitVal.toFixed(2))
          setFiatAmount(fiatAmtCalc.toFixed(2))
        }
      }
    }
  }

  function handleSaveChanges() {
    setSaving(true)
    setSaved(false)
    setSaveError("")

    editOrder(
      {
        cryptoAmount: parseFloat(cryptoAmount),
        fiatAmount: parseFloat(fiatAmount),
        cryptoRate: parseFloat(cryptoRate),
        profit: parseFloat(profit),
        txId: txId,
        orderType: orderType,
      },
      orderDetails.orderNumber
    )
      .then(res => {
        console.log("Order updated successfully:", res)
        // Reload order details from server to get updated data
        return getOrder(orderId)
      })
      .then(res => {
        setOrderDetails(res[0])
        setAllValues(res[0])
        setSaved(true)
        setChangesWasMade(false)
        setSaveError("")
      })
      .catch(err => {
        console.error("Error updating order:", err)
        setSaveError(t("Failed to save changes. Please try again."))
      })
      .finally(() => {
        setSaving(false)
      })
  }

  function handleConfirmFinish() {
    setShowConfirmModal(false)
    setClosing(true)
    closeOrder(orderDetails.orderNumber, txId)
      .then(r => {
        console.log("Order closed successfully:", r)
        window.location.reload()
      })
      .catch(err => {
        console.error("Error closing order:", err)
        setSaveError(t("Failed to close order. Please try again."))
        setClosing(false)
      })
  }

  function handleRefundClick() {
    setRefundError("")
    setRefundCode("")
    setShowRefundModal(true)

    // Generate and send code to Telegram
    generateRefundCode(orderDetails.orderNumber)
      .then(() => {
        console.log("Refund code sent to Telegram")
      })
      .catch(err => {
        console.error("Error generating refund code:", err)
        setRefundError(t("Failed to generate refund code. Please try again."))
      })
  }

  function handleConfirmRefund() {
    if (!refundCode) {
      setRefundError(t("Please enter the code"))
      return
    }

    setRefunding(true)
    setRefundError("")

    refundOrder(orderDetails.orderNumber, refundCode)
      .then(() => {
        console.log("Order refunded successfully")
        setShowRefundModal(false)
        window.location.reload()
      })
      .catch(err => {
        console.error("Error refunding order:", err)
        setRefundError(t("Invalid code or refund failed. Please try again."))
        setRefunding(false)
      })
  }

  function setAllValues(order) {
    setTxId(order.txId ?? "")
    setCryptoCurrencyCode(order.cryptoCurrencyCode ?? "")
    setCryptoAmount(order.cryptoAmount ?? "")
    setCryptoRate(order.cryptoRate ?? "")
    setFiatAmount(order.fiatAmount ?? "")
    setProfit(order.profit ?? "")
    setOrderType(order.orderType ?? "")
  }

  useEffect(() => {
    getOrder(orderId)
      .then(res => {
        console.log("Order data:", res)
        console.log("wasRefunded value:", res[0]?.wasRefunded)
        setOrderDetails(res[0])
        setAllValues(res[0])
        setEditable(res[0].status === "created")
      })
      .catch(err => {
        console.error("Error fetching order details:", err)
      })
  }, [])
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <h2>{t("Order Details")}</h2>
          <p>ID: {orderId}</p>
          {orderDetails && orderDetails.wasRefunded && (
            <div className="alert alert-warning" role="alert">
              <i className="mdi mdi-alert-outline me-2"></i>
              <strong>{t("Warning")}:</strong> {t("This order was refunded and re-edited. Client will not receive duplicate notification.")}
            </div>
          )}
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                {saved && (
                  <div className="alert alert-success" role="alert">
                    {t("Changes saved successfully!")}
                  </div>
                )}
                {saveError && (
                  <div className="alert alert-danger" role="alert">
                    {saveError}
                  </div>
                )}

                {editable && (
                  <div className="mb-3">
                    <label className="form-label">{t("Calculation Mode")}</label>
                    <div>
                      <div className="form-check form-check-inline" onClick={() => setCalculationMode("byAmount")} style={{cursor: 'pointer'}}>
                        <input
                          className="form-check-input"
                          type="radio"
                          id="modeByAmount"
                          checked={calculationMode === "byAmount"}
                          readOnly
                        />
                        <label className="form-check-label" htmlFor="modeByAmount" style={{cursor: 'pointer'}}>
                          {t("Calculate by Amount")}
                        </label>
                      </div>
                      <div className="form-check form-check-inline" onClick={() => setCalculationMode("byRate")} style={{cursor: 'pointer'}}>
                        <input
                          className="form-check-input"
                          type="radio"
                          id="modeByRate"
                          checked={calculationMode === "byRate"}
                          readOnly
                        />
                        <label className="form-check-label" htmlFor="modeByRate" style={{cursor: 'pointer'}}>
                          {t("Calculate by Rate")}
                        </label>
                      </div>
                    </div>
                    <small className="form-text text-muted">
                      {calculationMode === "byAmount"
                        ? t("Enter Crypto Amount and Fiat Amount to calculate Profit and Rate")
                        : t("Enter Rate and amount to calculate other values")}
                    </small>
                  </div>
                )}

                <table className={"table"}>
                  {orderDetails && (
                    <tbody className={styles.orderPropertiesTable}>
                    { (orderDetails.fullName || orderDetails.exchange) &&
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {orderDetails.exchange ? t("Exchange") : t("User")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {orderDetails.exchange || orderDetails.fullName}
                        </td>
                      </tr>
                    }
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Order Number")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {orderDetails.orderNumber}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Created At")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {new Date(
                            orderDetails.createdDate
                          ).toLocaleDateString()}{" "}
                          {new Date(
                            orderDetails.createdDate
                          ).toLocaleTimeString()}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Type")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {editable ? (
                            <select
                              className={"form-control form-control-sm"}
                              value={orderType}
                              name="orderType"
                              onChange={handleChanges}
                            >
                              <option value="buy">{t("Buy")}</option>
                              <option value="sell">{t("Sell")}</option>
                            </select>
                          ) : (
                            orderDetails.orderType.toUpperCase()
                          )}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Updated At")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {new Date(
                            orderDetails.updatedDate
                          ).toLocaleDateString()}{" "}
                          {new Date(
                            orderDetails.updatedDate
                          ).toLocaleTimeString()}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Status")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          <span
                            className={styles.status}
                            style={{
                              backgroundColor:
                                orderDetails.status === "finished"
                                  ? "green"
                                  : orderDetails.status === "created"
                                  ? "orange"
                                  : orderDetails.status === "cancelled"
                                  ? "red"
                                  : orderDetails.status === "expired"
                                  ? "blue"
                                  : "transparent",
                            }}
                          >
                            {t(orderDetails.status)}
                          </span>
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Cryptocurrency")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {editable ? (
                            <select
                              className={"form-control form-control-sm"}
                              value={cryptoCurrencyCode}
                              name="cryptoCurrencyCode"
                              onChange={handleChanges}
                            >
                              <option value="">{t("Select Cryptocurrency")}</option>
                              <option value="BTC">{t("Bitcoin (BTC)")}</option>
                              <option value="USDT">{t("Tether (USDT)")}</option>
                            </select>
                          ) : (
                            orderDetails.cryptoCurrencyCode
                          )}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Crypto Amount")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {editable ? (
                            <input
                              className={"form-control form-control-sm"}
                              value={cryptoAmount}
                              name="cryptoAmount"
                              type="number"
                              onChange={handleChanges}
                              disabled={calculationMode === "byRate" && orderType === "buy"}
                              readOnly={calculationMode === "byRate" && orderType === "buy"}
                            />
                          ) : (
                            orderDetails.cryptoAmount
                          )}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Crypto Rate")} (%)
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {editable ? (
                            <>
                              <input
                                className={"form-control form-control-sm"}
                                value={cryptoRate}
                                name="cryptoRate"
                                type="number"
                                step="0.25"
                                onChange={handleChanges}
                                disabled={calculationMode === "byAmount"}
                                readOnly={calculationMode === "byAmount"}
                              />
                              {calculationMode === "byAmount" && (
                                <small className="form-text text-muted">
                                  {t("Calculated automatically")}
                                </small>
                              )}
                            </>
                          ) : (
                            orderDetails.cryptoRate
                          )}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Fiat Currency")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          USD
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Fiat Amount")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {editable ? (
                            <input
                              className={"form-control form-control-sm"}
                              value={fiatAmount}
                              name="fiatAmount"
                              type="number"
                              onChange={handleChanges}
                              disabled={calculationMode === "byRate" && orderType === "sell"}
                              readOnly={calculationMode === "byRate" && orderType === "sell"}
                            />
                          ) : (
                            orderDetails.fiatAmount
                          )}
                        </td>
                      </tr>
                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Profit")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {editable ? (
                            <>
                              <input
                                className={"form-control form-control-sm"}
                                value={profit}
                                name="profit"
                                type="number"
                                readOnly
                                disabled
                              />
                              <small className="form-text text-muted">
                                {t("Calculated automatically")}
                              </small>
                            </>
                          ) : (
                            orderDetails.profit
                          )}
                        </td>
                      </tr>
                      {orderDetails.invoice && (
                        <tr className={styles.row}>
                          <th scope={"col"} className={styles.label}>
                            {t("Invoice URL")}
                          </th>
                          <td scope={"col"} className={styles.value}>
                            <a
                              href={orderDetails.invoice}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("Download")}
                            </a>
                          </td>
                        </tr>
                      )}

                      <tr className={styles.row}>
                        <th scope={"col"} className={styles.label}>
                          {t("Txid")}
                        </th>
                        <td scope={"col"} className={styles.value}>
                          {orderDetails.txId ? (
                            orderDetails.txId
                          ) : orderDetails.status === "expired" ||
                            orderDetails === "cancelled" ? (
                            t("N/A")
                          ) : orderDetails.status === "created" ? (
                            <input
                              className={"form-control form-control-sm"}
                              value={txId}
                              name="txId"
                              onChange={handleChanges}
                            />
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
              <div
                className="form-group"
                style={{ display: "flex", flexDirection: "row", gap: "10px" }}
              >
                {orderDetails && orderDetails.status === "created" && (
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      // Here you would typically cancel the order
                      console.log("Cancelling order...")
                      cancelOrder(orderDetails.orderNumber)
                        .then(r => {
                          console.log("Order cancelled successfully:", r)
                          window.location.reload()
                        })
                        .catch(err => {
                          console.error("Error cancelling order:", err)
                          setSaveError(
                            t("Failed to cancel order. Please try again.")
                          )
                        })
                    }}
                  >
                    {t("Cancel Order")}
                  </button>
                )}
                {orderDetails && orderDetails.status === "created" && (
                  <button
                    className="btn btn-success"
                    onClick={handleSaveChanges}
                    disabled={saving}
                  >
                    {saving ? t("Saving...") : t("Save Changes")}
                  </button>
                )}

                {orderDetails && txId && orderDetails.status === "created" && saved && (
                  <button
                    className="btn btn-success"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={closing}
                  >
                    {closing ? (
                      <Spinner className="ms-2 mx-2" color="light" size={"sm"} />
                    ) : (
                      t("Finish Order")
                    )}
                  </button>
                )}

                {orderDetails && orderDetails.status === "finished" && (
                  <button
                    className="btn btn-warning"
                    onClick={handleRefundClick}
                    disabled={refunding}
                  >
                    {t("Refund Order")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Container>

        {/* Confirmation Modal */}
        <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)} size="lg">
          <ModalHeader toggle={() => setShowConfirmModal(false)}>
            {t("Confirm Order Completion")}
          </ModalHeader>
          <ModalBody>
            <p>{t("Please verify all order details before finishing:")}</p>
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th>{t("Order Number")}</th>
                  <td>{orderDetails?.orderNumber}</td>
                </tr>
                <tr>
                  <th>{t("Type")}</th>
                  <td>{orderDetails?.orderType?.toUpperCase()}</td>
                </tr>
                <tr>
                  <th>{t("Crypto Amount")}</th>
                  <td>{cryptoAmount} {cryptoCurrencyCode}</td>
                </tr>
                <tr>
                  <th>{t("Crypto Rate")} (%)</th>
                  <td>{cryptoRate}%</td>
                </tr>
                <tr>
                  <th>{t("Fiat Amount")}</th>
                  <td>${fiatAmount}</td>
                </tr>
                <tr>
                  <th>{t("Profit")}</th>
                  <td>${profit}</td>
                </tr>
                <tr>
                  <th>{t("Txid")}</th>
                  <td style={{wordBreak: 'break-all'}}>{txId}</td>
                </tr>
              </tbody>
            </table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowConfirmModal(false)}>
              {t("Cancel")}
            </Button>
            <Button color="success" onClick={handleConfirmFinish} disabled={closing}>
              {closing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("Finishing...")}
                </>
              ) : (
                t("Confirm & Finish")
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Refund Modal with 2FA */}
        <Modal isOpen={showRefundModal} toggle={() => setShowRefundModal(false)}>
          <ModalHeader toggle={() => setShowRefundModal(false)}>
            {t("Refund Order - 2FA Required")}
          </ModalHeader>
          <ModalBody>
            <p>{t("A refund code has been sent to your Telegram. Please enter the code to confirm the refund.")}</p>
            <p className="text-warning"><strong>{t("Warning: This will reverse the order and restore the balance.")}</strong></p>

            {refundError && (
              <div className="alert alert-danger" role="alert">
                {refundError}
              </div>
            )}

            <FormGroup>
              <Label for="refundCode">{t("Enter Refund Code")}</Label>
              <Input
                type="text"
                id="refundCode"
                value={refundCode}
                onChange={(e) => setRefundCode(e.target.value)}
                placeholder={t("Enter 5-digit code")}
                maxLength={5}
                disabled={refunding}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowRefundModal(false)} disabled={refunding}>
              {t("Cancel")}
            </Button>
            <Button color="warning" onClick={handleConfirmRefund} disabled={refunding || !refundCode}>
              {refunding ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("Refunding...")}
                </>
              ) : (
                t("Confirm Refund")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  )
}

OrderDetailsPage.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(OrderDetailsPage)